<!-- Polaris (fase 5.2, R2-5-PC-5 = A): "New contact" in the kit's EditorDrawer (fase P), with the woot's own
     ContactsForm inside (same fields, validation and duplicate messages as the old CreateNewContactDialog, which it
     replaces). The name is a pair of fields in the form, so the drawer's title is text, not a field. Save waits for a
     change and a valid form; leaving with changes asks "Discard changes?" (R2-5-PC-4 = B, useDiscardGuard). The page
     lays it out beside the list (a column from 1280px, the overlay below) and never opens it over another drawer. -->
<script setup>
import { computed, ref, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import {
  DuplicateContactException,
  ExceptionWithMessage,
} from 'shared/helpers/CustomErrors';
import EditorDrawer from 'dashboard/components-next/polaris/cards/EditorDrawer.vue';
import ContactsForm from 'dashboard/components-next/Contacts/ContactsForm/ContactsForm.vue';

const open = defineModel('open', { type: Boolean, default: false });

const { t } = useI18n();
const store = useStore();
const uiFlags = useMapGetter('contacts/getUIFlags');

const formRef = ref(null);
const editorRef = ref(null);
const initial = ref('');
const snapshot = () => JSON.stringify(formRef.value?.state ?? {});

// The form mounts with the drawer: its blank state is the starting point.
watch(open, async value => {
  if (!value) return;
  await nextTick();
  initial.value = snapshot();
});

const dirty = computed(() => !!formRef.value && snapshot() !== initial.value);
const name = computed(() => formRef.value?.state.name?.trim() ?? '');
const canSave = computed(
  () =>
    !!name.value && !formRef.value?.isFormInvalid && !uiFlags.value.isCreating
);

const onSave = async () => {
  const { firstName, lastName, ...contact } = formRef.value.state;
  const i18nPrefix = 'CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION';
  try {
    await store.dispatch('contacts/create', contact);
    useAlert(t(`${i18nPrefix}.SUCCESS_MESSAGE`));
    open.value = false;
  } catch (error) {
    if (error instanceof DuplicateContactException) {
      if (error.data.includes('email')) {
        useAlert(t(`${i18nPrefix}.EMAIL_ADDRESS_DUPLICATE`));
      } else if (error.data.includes('phone_number')) {
        useAlert(t(`${i18nPrefix}.PHONE_NUMBER_DUPLICATE`));
      }
    } else if (error instanceof ExceptionWithMessage) {
      useAlert(error.data);
    } else {
      useAlert(t(`${i18nPrefix}.ERROR_MESSAGE`));
    }
  }
};

// The page switches to a contact through the same guard (5-PC-5: never two drawers at once).
defineExpose({ runGuarded: action => editorRef.value?.runGuarded(action) });
</script>

<template>
  <EditorDrawer
    ref="editorRef"
    v-model:open="open"
    :title="name"
    :kind="t('CONTACTS_POLARIS.NEW_CONTACT.KIND')"
    :title-placeholder="
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.ADD_CONTACT')
    "
    :title-editable="false"
    :dirty="dirty"
    :can-save="canSave"
    :save-label="
      t('CONTACTS_LAYOUT.HEADER.ACTIONS.CONTACT_CREATION.SAVE_CONTACT')
    "
    data-new-contact-drawer
    @save="onSave"
  >
    <ContactsForm ref="formRef" is-new-contact is-details-view />
  </EditorDrawer>
</template>
